import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, Sprout, Users, Crown } from "lucide-react";
import { GFFrame } from "@/components/GFFrame";

const PathCard = ({ testId, onClick, theme, title, subtitle, icon: Icon, delay, image, price, imgPos = "center" }) => (
  <motion.button
    data-testid={testId}
    onClick={onClick}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="group relative w-full overflow-hidden rounded-3xl text-left transition-transform duration-200 active:scale-[0.98] hover:scale-[1.01]"
    style={{ background: theme.bg }}
  >
    <div className="relative h-36 w-full overflow-hidden">
      <img src={image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ objectPosition: imgPos }} />
      <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 30%, ${theme.bg} 100%)` }} />
      {price && (
        <span className="absolute right-3 top-3 rounded-full px-3 py-1 font-heading text-sm font-bold text-[#0F172A] shadow-lg" style={{ background: theme.accent }} data-testid={`${testId}-price`}>
          {price}
        </span>
      )}
    </div>
    <div className="absolute -right-6 top-24 h-28 w-28 rounded-full opacity-20" style={{ background: theme.glow }} />
    <div className="relative z-10 -mt-6 px-6 pb-6">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.14)" }}>
        <Icon className="h-5 w-5" style={{ color: theme.accent }} />
      </span>
      <h3 className="mt-3 font-heading text-2xl font-bold text-white">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed" style={{ color: theme.sub }}>{subtitle}</p>
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold" style={{ color: theme.accent }}>
        {price ? "Book Your Session" : "Mula"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </motion.button>
);

export default function Landing() {
  const navigate = useNavigate();
  return (
    <GFFrame testId="landing-page">
      <div className="flex min-h-screen flex-col bg-[#F4F1EA] px-6 pb-10 pt-12">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-2"
        >
          <span className="font-heading text-sm font-bold tracking-[0.35em] text-[#0F172A]">GROWTH</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
          <span className="font-heading text-sm font-bold tracking-[0.35em] text-[#D4AF37]">FACTOR</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 text-center"
        >
          <div className="mx-auto gf-gold-line" />
          <h1 className="mt-5 font-heading text-4xl font-bold leading-tight text-[#0F172A]">
            Apakah yang anda ingin nilai hari ini?
          </h1>
          <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-slate-500">
            Empat perjalanan premium dalam satu ekosistem Growth Factor. Pilih perjalanan anda.
          </p>
        </motion.div>

        <div className="mt-8 flex flex-1 flex-col justify-center gap-4">
          <PathCard
            testId="start-assessment-btn"
            onClick={() => navigate("/mula")}
            delay={0.2}
            icon={Briefcase}
            title="Business Growth"
            subtitle="Grow your business with clarity — diagnosis pertumbuhan bisnes, kewangan, pasaran, jenama & pelaksanaan."
            image="https://customer-assets-4nw71qhi.emergentagent.net/job_factor-growth-hub/artifacts/p9xkq6x5_WhatsApp%20Image%202026-09-11%20at%205.41.08%20PM.jpeg"
            theme={{ bg: "#0F172A", glow: "#D4AF37", accent: "#D4AF37", sub: "#94A3B8" }}
          />
          <PathCard
            testId="choose-personal-btn"
            onClick={() => navigate("/personal/mula")}
            delay={0.3}
            icon={Sprout}
            title="Personal Growth"
            subtitle="Understand yourself. Grow with purpose — diagnosis perkembangan diri, hala tuju, daya tahan & tindakan."
            image="https://customer-assets-4nw71qhi.emergentagent.net/job_factor-growth-hub/artifacts/g5pkww1n_WhatsApp%20Image%202026-09-11%20at%205.42.29%20PM.jpeg"
            imgPos="center 30%"
            theme={{ bg: "#064E3B", glow: "#D4AF37", accent: "#F0C750", sub: "#A7C7B8" }}
          />
          <PathCard
            testId="choose-cohort-btn"
            onClick={() => navigate("/cohort")}
            delay={0.4}
            icon={Users}
            title="Organisation / Cohort"
            subtitle="Fahami pasukan anda. Kukuhkan organisasi anda — penilaian cohort berasaskan data untuk memahami kekuatan & kesediaan pasukan."
            image="https://customer-assets-4nw71qhi.emergentagent.net/job_factor-growth-hub/artifacts/ncrv8lbh_WhatsApp%20Image%202026-09-11%20at%205.43.24%20PM.jpeg"
            theme={{ bg: "#4B006E", glow: "#D4AF37", accent: "#F0C750", sub: "#C9B2D6" }}
          />
          <PathCard
            testId="choose-clarity-btn"
            onClick={() => navigate("/business-clarity")}
            delay={0.5}
            icon={Crown}
            title="Business Clarity Session with Dr. Irma"
            subtitle="One business. One conversation. A clearer way forward — sesi mentoring online 60 minit + akses Business Growth + 50 kredit AI Campaign Lab (sah 3 bulan)."
            image="https://customer-assets-4nw71qhi.emergentagent.net/job_factor-growth-hub/artifacts/ll2g7xvp_WhatsApp%20Image%202026-09-11%20at%205.45.20%20PM.jpeg"
            imgPos="center 20%"
            price="RM500"
            theme={{ bg: "#3B2A20", glow: "#B8860B", accent: "#D9A441", sub: "#CBB79E" }}
          />
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Tanpa pendaftaran akaun · Terus di pelayar anda
        </p>
      </div>
    </GFFrame>
  );
}
